import React, { useState, useEffect, useRef } from "react";
import { Link, useStaticQuery, graphql } from "gatsby";
import { useLocation } from "@reach/router";
import { Helmet } from "react-helmet";
import "./layout.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faYoutube, 
  faInstagram, 
  faTiktok, 
  faFacebook, 
  faThreads 
} from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faUser, faCampground, faPersonHiking, faCaravan, faTruckMonster } from '@fortawesome/free-solid-svg-icons';
import { faMap } from '@fortawesome/free-regular-svg-icons';
import { faTable } from '@fortawesome/free-solid-svg-icons';
import { faBookOpen } from '@fortawesome/free-solid-svg-icons';
import { faCompass } from '@fortawesome/free-solid-svg-icons'; // Import the compass icon
import NotificationBar from './notifications';  // Make sure this matches your file name
import CookieConsent from './CookieConsent';  // Import the CookieConsent component
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'; // Add this import
import { useAuth } from '../context/AuthContext';

// Import your local header image for the site title
import siteTitleImage from "../images/logos/sodclear.png"; // SOD clear logo
import img1 from "../images/assets/img-1.png";
import img2 from "../images/assets/img-2.png";
import img3 from "../images/assets/img-3.png";
import img4 from "../images/assets/img-4.png";
import img5 from "../images/assets/img-5.png";
import img6 from "../images/assets/img-6.png";
import img7 from "../images/assets/img-7.png";

export default function Layout({ children }) {
  const location = useLocation();
  const isMapPage = location.pathname === "/campground-viewer";
  const { user, loading, signOut, signInWithMagicLink } = useAuth();
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState(null);
  const isHomePage = location.pathname === "/";
  
  // Skip rendering user-specific UI during SSR
  const isSSR = typeof window === 'undefined';
  
  // Only use these state hooks if we're in the browser
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Tooltip state
  const [tooltipText, setTooltipText] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [showTooltip, setShowTooltip] = useState(false);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleUserMenu = () => setUserMenuOpen(!userMenuOpen);

  // Close user menu when clicking outside
  const userMenuRef = useRef(null);
  
  // Add ref for the sidebar
  const sidebarRef = useRef(null);
  
  // Add reactive mobile detection state
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (isSSR) return;
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isSSR]);

  // Function to close sidebar on mobile
  const closeSidebarOnMobile = () => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  // Tooltip handlers
  const handleMouseEnter = (e, tooltipContent) => {
    if (sidebarOpen) return; // Only show tooltips when sidebar is collapsed
    
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipText(tooltipContent);
    setTooltipPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 10 // Position to the right of the sidebar item
    });
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };
  
  useEffect(() => {
    if (isSSR) return; // Skip effect during SSR
    
    function handleClickOutside(event) {
      // Handle user menu
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      
      // Handle sidebar - close if clicked outside on mobile
      if (sidebarRef.current && 
          sidebarOpen && 
          !sidebarRef.current.contains(event.target) && 
          !event.target.closest('.header__menu-trigger') && 
          isMobile) {
        setSidebarOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSSR, sidebarOpen]);

  // Add window resize listener to update mobile detection
  useEffect(() => {
    if (isSSR) return; // Skip effect during SSR
    
    const handleResize = () => {
      // If transitioning from mobile to desktop with sidebar open, keep it open
      // If the sidebar is closed, no action needed
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isSSR]);

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
  };

  /**
   * Normalize both the current path and the provided route by removing any trailing slash,
   * then compare for an exact match. This ensures links are highlighted whether they're
   * visited as "/about" or "/about/".
   */
  function isActiveLink(route) {
    // Remove trailing slashes for a consistent comparison
    const currentPath = location.pathname.replace(/\/$/, "");
    const targetRoute = route.replace(/\/$/, "");
    
    // For Guides parent and its subpages
    if (route === "/guides") {
      // Only highlight the parent when exactly on /guides
      return currentPath === "/guides" ? "active" : "";
    }
    
    // For subpages, only highlight when exactly on that page
    if (route.startsWith("/guides/")) {
      return currentPath === targetRoute ? "active" : "";
    }
    
    // For all other routes, exact match only
    return currentPath === targetRoute ? "active" : "";
  }

  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  // Add this array of searchable locations
  const locations = [
    { path: "/campground-viewer", name: "Best Campgrounds", titles: ["Best Campgrounds", "Campground Viewer", "Campground Locations"] },
    { path: "/guides", name: "Guides", titles: ["Guides", "Outdoor Guides", "Beginner Guides"] },
    { path: "/contact-us", name: "Contact Us", titles: ["Contact Us", "Get in Touch", "Support"] }
  ];

  // Update the GraphQL query
  const data = useStaticQuery(graphql`
    query {
      allSitePage {
        nodes {
          path
          pageContext
        }
      }
      allNotificationsCsv {
        nodes {
          notification
          url
        }
      }
    }
  `);

  // Update how we combine locations with better error handling
  const allLocations = [
    ...locations,  // Keep our static locations
    ...(data?.allSitePage?.nodes || [])
      .filter(node => 
        // Only include pages that have a pageContext.newTitle
        node?.pageContext?.newTitle && 
        // And are basic guides
        node?.path?.startsWith('/guides/')
      )
      .map(node => ({
        path: node.path,
        name: node.pageContext.newTitle,  // Use the actual title
        titles: [
          node.pageContext.newTitle,
          node.pageContext.introParagraph || ''
        ]
      }))
  ];

  // Get notifications from CSV data
  const notifications = data.allNotificationsCsv.nodes;

  // Update the filter function
  const filteredLocations = allLocations.filter(location => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      location.name.toLowerCase().includes(searchTermLower) ||
      (location.titles && location.titles.some(title => 
        title.toLowerCase().includes(searchTermLower)
      ))
    );
  });

  // Add closeSidebarOnMobile to handleSearchResultClick
  const handleSearchResultClick = (path) => {
    setSearchTerm('');
    setIsSearchModalOpen(false);
    closeSidebarOnMobile();
  };

  // Add this function to handle search icon click
  const handleSearchIconClick = () => {
    setIsSearchModalOpen(true);
    setSidebarOpen(false);
  };

  // Add this function to handle modal close
  const handleModalClose = () => {
    setIsSearchModalOpen(false);
    setSearchTerm('');
  };

  // JS-driven ripple on click
  const handleRipple = (e) => {
    const el = e.currentTarget;
    const existing = el.querySelector('.ripple');
    if (existing) existing.remove();
    const circle = document.createElement('span');
    circle.className = 'ripple';
    el.appendChild(circle);
    setTimeout(() => circle.remove(), 800);
  };

  // Add this function to handle escape key
  useEffect(() => {
    if (isSSR) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleModalClose();
      }
    };

    if (isSearchModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isSearchModalOpen, isSSR]);

  // Modify the search results JSX
  const searchResults = (
    <div className="search-results">
      <h2>Search Results for "{searchTerm}"</h2>
      {filteredLocations.length > 0 ? (
        <ul className="search-results-list">
          {filteredLocations.map(location => (
            <li key={location.path}>
              <Link 
                to={location.path}
                onClick={() => handleSearchResultClick(location.path)}
              >
                {location.name}
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p>No locations found matching "{searchTerm}"</p>
      )}
    </div>
  );

  // Add the search modal JSX
  const searchModal = isSearchModalOpen && (
    <div className="search-modal-overlay" onClick={handleModalClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="search-modal-content" onClick={e => e.stopPropagation()} style={{
        backgroundColor: '#1f1f1f',
        padding: '2rem',
        borderRadius: '8px',
        width: '600px',
        height: '400px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}>
        <div className="search-input-container" style={{ marginBottom: '1.5rem' }}>
          <input
            type="text"
            className="search-input"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              fontFamily: "'Geist Mono', monospace",
              width: '100%',
              padding: '0.75rem 1rem',
              fontSize: '1.2rem',
              backgroundColor: 'rgba(132, 175, 163, 0.2)',
              border: '2px solid rgba(132, 175, 163, 0.4)',
              borderRadius: '4px',
              color: '#fff',
              outline: 'none'
            }}
            autoFocus
          />
        </div>
        <div style={{ 
          flex: 1, 
          overflowY: 'auto',
          paddingRight: '0.5rem'
        }}>
          {searchTerm && (
            <div className="search-results">
              <h2 style={{ 
                fontSize: '1.2rem', 
                marginBottom: '1rem',
                color: '#fff'
              }}>Search Results for "{searchTerm}"</h2>
              {filteredLocations.length > 0 ? (
                <ul className="search-results-list" style={{ 
                  listStyle: 'none', 
                  padding: 0, 
                  margin: 0 
                }}>
                  {filteredLocations.map(location => (
                    <li key={location.path} style={{ 
                      marginBottom: '0.75rem' 
                    }}>
                      <Link 
                        to={location.path}
                        onClick={() => handleSearchResultClick(location.path)}
                        style={{
                          color: '#fff',
                          textDecoration: 'none',
                          display: 'block',
                          padding: '0.5rem',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={e => e.currentTarget.style.backgroundColor = 'rgba(132, 175, 163, 0.2)'}
                        onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {location.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ color: '#fff' }}>No locations found matching "{searchTerm}"</p>
              )}
            </div>
          )}
        </div>
        <button
          onClick={handleModalClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'none',
            border: 'none',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '1.5rem',
            padding: '0.5rem',
            opacity: 0.7,
            transition: 'opacity 0.2s'
          }}
          onMouseOver={e => e.currentTarget.style.opacity = '1'}
          onMouseOut={e => e.currentTarget.style.opacity = '0.7'}
        >
          ×
        </button>
      </div>
    </div>
  );

  // Trail updates dropdown removed

  // Add this new state for Trail Reports dropdown
  const [isReportsDropdownOpen, setIsReportsDropdownOpen] = useState(false);

  // Add useEffect for logging
  useEffect(() => {
    console.log("Header children:", document.querySelector('.header')?.children);
  }, []);

  const subscribeButtonRef = useRef(null);

  useEffect(() => {
    if (subscribeButtonRef.current) {
      const computedStyle = getComputedStyle(subscribeButtonRef.current);
      console.log("Subscribe Button Border Width:", computedStyle.borderWidth);
      console.log("Subscribe Button Border Color:", computedStyle.borderColor);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const { error: signInError } = await signInWithMagicLink(email);
      if (signInError) throw signInError;
      setSubmitSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Rotating background images for signup popup
  const bgImages = [img1, img2, img3, img4, img5, img6, img7];
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  useEffect(() => {
    const timer = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentBgIndex(prev => (prev + 1) % bgImages.length);
        setIsTransitioning(false);
      }, 1000);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Throttle signup popup: show once every 5 days per user/device
  useEffect(() => {
    if (user || loading || isHomePage || isSSR) return;
    const last = localStorage.getItem('lastSignupPopupDismissed');
    const shouldShow = !last || (Date.now() - parseInt(last, 10) > 5 * 24 * 60 * 60 * 1000);
    if (shouldShow) setShowLoginPopup(true);
  }, [user, loading, isHomePage, isSSR]);

  return (
    <div className="layout">
      {searchModal}
      <Helmet>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=5, viewport-fit=cover"
        />
        <link rel="profile" href="https://gmpg.org/xfn/11" />
        <title>Sierra Outdoors</title>
        <style>
          {`
          body, html { 
            margin: 0; 
            padding: 0; 
            overflow-x: hidden;
          }
          `}
        </style>
      </Helmet>

      {/* Header */}
      <header className="header">
        <button
          className="header__menu-trigger"
          onClick={toggleSidebar}
          aria-label="Toggle Sidebar"
        >
          <svg
            focusable="false"
            preserveAspectRatio="xMidYMid meet"
            xmlns="http://www.w3.org/2000/svg"
            fill="#fff"
            viewBox="0 0 32 32"
            aria-hidden="true"
          >
            <path d="M4 8h24v2H4zm0 7h24v2H4zm0 7h24v2H4z"></path>
          </svg>
        </button>

        {/* Site title: using the local image */}
        <div className="wordpress-header">
          <a href="https://sierraoutdoors.org" onClick={closeSidebarOnMobile}>
            <img
              src={siteTitleImage}
              alt="Sierra Outdoors"
              className="header-site-title"
            />
          </a>
        </div>

        {/* Move the user menu to the right side */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* User Menu - No spinner during loading */}
          {!loading && user ? (
            <div className="user-menu-container" ref={userMenuRef}>
              <button 
                className="user-icon" 
                onClick={toggleUserMenu}
                aria-label="User menu"
                style={{
                  fontFamily: "'Geist Mono', monospace",
                  fontSize: "1.2rem",
                  fontWeight: "500",
                  border: "2px solid rgba(255, 255, 255, 0.5)"
                }}
              >
                {user.email.charAt(0).toUpperCase()}
              </button>
              {userMenuOpen && (
                <div className="user-dropdown">
                  <div className="user-info">
                    <span className="user-email">{user.email}</span>
                  </div>
                  <ul>
                    <li>
                      <Link to="/profile" onClick={() => {
                        setUserMenuOpen(false);
                        closeSidebarOnMobile();
                      }}>Settings</Link>
                    </li>
                    <li>
                      <button onClick={handleSignOut}>Sign Out</button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <Link 
              to="/login" 
              className="subscribe-header-button" 
              style={{ 
                border: '2px solid white',
                backgroundColor: '#2eb488'
              }}
              onClick={closeSidebarOnMobile}
            >
              LOG IN
            </Link>
          )}

          <Link 
            to="/signup" 
            className="subscribe-header-button" 
            style={{ border: '2px solid #2eb488', display: 'none' }}
            ref={subscribeButtonRef}
            onClick={closeSidebarOnMobile}
          >
            COMING SOON
          </Link>
        </div>
      </header>

      <NotificationBar notifications={notifications} />
      
      {/* Body: Sidebar + Main Content */}
      <div className="layout__body">
        {/* Custom Tooltip */}
        {showTooltip && (
          <div 
            className="sidebar-tooltip" 
            style={{
              position: 'fixed',
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: 'translateY(-50%)',
              background: 'rgba(10, 10, 10, 0.35)', // Very transparent dark background
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)', // For Safari support
              color: 'rgba(255, 255, 255, 0.95)',
              padding: '5px 10px',
              borderRadius: '6px',
              whiteSpace: 'nowrap',
              boxShadow: 'none',
              zIndex: 9999,
              fontSize: '13px',
              letterSpacing: '0.01em',
              fontWeight: '400',
              border: 'none',
              pointerEvents: 'none',
              animation: 'tooltipFade 0.15s ease-out',
              opacity: 0.95,
              fontFamily: "'Geist', sans-serif", // Use your site's font
              maxWidth: '180px',
              textAlign: 'center',
              textShadow: 'none'
            }}
          >
            {tooltipText}
          </div>
        )}
        <aside className={`sidebar ${sidebarOpen ? "open" : "closed"}`} ref={sidebarRef}>
          {!sidebarOpen ? (
            // Collapsed sidebar - icons only
            <nav>
              <div className="search-container">
                <div 
                  className="search-icon"
                  onClick={handleSearchIconClick}
                  role="button"
                  tabIndex={0}
                >
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </div>
              </div>
              <ul className="nav-list">
                <li className={`nav-item home ${isActiveLink("/")}`}>
                  <Link 
                    to="/" 
                    onClick={closeSidebarOnMobile}
                    onMouseEnter={(e) => handleMouseEnter(e, "Home")}
                    onMouseLeave={handleMouseLeave}
                  >
                    <span className="icon">
                      <FontAwesomeIcon icon={faCampground} />
                    </span>
                  </Link>
                </li>
                <li className={`nav-item ${isActiveLink("/campground-viewer")}`}>
                  <Link 
                    to="/campground-viewer" 
                    onClick={closeSidebarOnMobile}
                    onMouseEnter={(e) => handleMouseEnter(e, "Best Campgrounds")}
                    onMouseLeave={handleMouseLeave}
                  >
                    <span className="icon">
                      <FontAwesomeIcon icon={faMap} />
                    </span>
                  </Link>
                </li>
                <li className={`nav-item ${isActiveLink("/guides")}`}>
                  <Link 
                    to="/guides" 
                    onClick={closeSidebarOnMobile}
                    onMouseEnter={(e) => handleMouseEnter(e, "Explore Guides")}
                    onMouseLeave={handleMouseLeave}
                  >
                    <span className="icon">
                      <FontAwesomeIcon icon={faBookOpen} />
                    </span>
                  </Link>
                </li>
                <li className={`nav-item ${isActiveLink("/hiking-guides")}`} style={{ opacity: 0.6 }}>
                  <Link 
                    to="/hiking-guides" 
                    style={{ pointerEvents: 'none' }} 
                    onClick={closeSidebarOnMobile}
                    onMouseEnter={(e) => handleMouseEnter(e, "Hiking Guides")}
                    onMouseLeave={handleMouseLeave}
                  >
                    <span className="icon">
                      <FontAwesomeIcon icon={faPersonHiking} />
                    </span>
                  </Link>
                </li>
                <li className={`nav-item ${isActiveLink("/backpacking-guides")}`} style={{ opacity: 0.6 }}>
                  <Link 
                    to="/backpacking-guides" 
                    style={{ pointerEvents: 'none' }} 
                    onClick={closeSidebarOnMobile}
                    onMouseEnter={(e) => handleMouseEnter(e, "Backpacking Guides")}
                    onMouseLeave={handleMouseLeave}
                  >
                    <span className="icon">
                      <FontAwesomeIcon icon={faCompass} />
                    </span>
                  </Link>
                </li>
                <li className={`nav-item ${isActiveLink("/beginner-overlanding-guides")}`} style={{ opacity: 0.6 }}>
                  <Link 
                    to="/beginner-overlanding-guides" 
                    style={{ pointerEvents: 'none' }} 
                    onClick={closeSidebarOnMobile}
                    onMouseEnter={(e) => handleMouseEnter(e, "Beginner Overlanding")}
                    onMouseLeave={handleMouseLeave}
                  >
                    <span className="icon">
                      <FontAwesomeIcon icon={faCaravan} />
                    </span>
                  </Link>
                </li>
                <li className={`nav-item ${isActiveLink("/intermediate-overlanding-guides")}`} style={{ opacity: 0.6 }}>
                  <Link 
                    to="/intermediate-overlanding-guides" 
                    style={{ pointerEvents: 'none' }} 
                    onClick={closeSidebarOnMobile}
                    onMouseEnter={(e) => handleMouseEnter(e, "Intermediate Overlanding")}
                    onMouseLeave={handleMouseLeave}
                  >
                    <span className="icon">
                      <FontAwesomeIcon icon={faTruckMonster} />
                    </span>
                  </Link>
                </li>
                <li className={`nav-item ${isActiveLink("/contact-us")}`}>
                  <Link 
                    to="/contact-us" 
                    onClick={closeSidebarOnMobile}
                    onMouseEnter={(e) => handleMouseEnter(e, "Contact Us")}
                    onMouseLeave={handleMouseLeave}
                  >
                    <span className="icon">
                      <FontAwesomeIcon icon={faEnvelope} />
                    </span>
                  </Link>
                </li>
              </ul>
            </nav>
          ) : (
            // Expanded sidebar
            <>
              <div className="search-container">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={handleSearchIconClick}
                  readOnly
                  style={{
                    fontFamily: "'Geist Mono', monospace"
                  }}
                />
              </div>
              <ul className="nav-list">
                <li className={`nav-item home ${isActiveLink("/")}`}>
                  <Link to="/" onClick={closeSidebarOnMobile}>
                    <span className="icon">
                      <FontAwesomeIcon icon={faCampground} />
                    </span>
                    Home
                  </Link>
                </li>
                <li className={`nav-item ${isActiveLink("/campground-viewer")}`}>
                  <Link to="/campground-viewer" onClick={closeSidebarOnMobile}>
                    <span className="icon">
                      <FontAwesomeIcon icon={faMap} />
                    </span>
                    Best Campgrounds
                  </Link>
                </li>
                <li className={`nav-item ${isActiveLink("/guides")}`}>
                  <Link to="/guides" onClick={closeSidebarOnMobile}>
                    <span className="icon">
                      <FontAwesomeIcon icon={faBookOpen} />
                    </span>
                    Explore Guides
                  </Link>
                </li>
                <li className={`nav-item ${isActiveLink("/hiking-guides")}`} style={{ opacity: 0.6 }}>
                  <Link to="/hiking-guides" style={{ pointerEvents: 'none' }} onClick={closeSidebarOnMobile}>
                    <span className="icon">
                      <FontAwesomeIcon icon={faPersonHiking} />
                    </span>
                    Hiking Guides
                  </Link>
                </li>
                <li className={`nav-item ${isActiveLink("/backpacking-guides")}`} style={{ opacity: 0.6 }}>
                  <Link to="/backpacking-guides" style={{ pointerEvents: 'none' }} onClick={closeSidebarOnMobile}>
                    <span className="icon">
                      <FontAwesomeIcon icon={faCompass} />
                    </span>
                    Backpacking Guides
                  </Link>
                </li>
                <li className={`nav-item ${isActiveLink("/beginner-overlanding-guides")}`} style={{ opacity: 0.6 }}>
                  <Link to="/beginner-overlanding-guides" style={{ pointerEvents: 'none' }} onClick={closeSidebarOnMobile}>
                    <span className="icon">
                      <FontAwesomeIcon icon={faCaravan} />
                    </span>
                    Beginner Overlanding
                  </Link>
                </li>
                <li className={`nav-item ${isActiveLink("/intermediate-overlanding-guides")}`} style={{ opacity: 0.6 }}>
                  <Link to="/intermediate-overlanding-guides" style={{ pointerEvents: 'none' }} onClick={closeSidebarOnMobile}>
                    <span className="icon">
                      <FontAwesomeIcon icon={faTruckMonster} />
                    </span>
                    Intermediate Overlanding
                  </Link>
                </li>
                <li className={`nav-item ${isActiveLink("/contact-us")}`}>
                  <Link to="/contact-us" onClick={closeSidebarOnMobile}>
                    <span className="icon">
                      <FontAwesomeIcon icon={faEnvelope} />
                    </span>
                    Contact Us
                  </Link>
                </li>
              </ul>
            </>
          )}
        </aside>

        <main 
          className="content" 
          style={
            location.pathname === "/" ? {
              margin: 0,
              padding: 0,
              minHeight: "calc(100vh - 72px)",
              height: "100%",
              width: "100%"
            } : {}
          }
          data-page={location.pathname.replace(/^\//, '')}
        >
          {location.pathname === "/campground-viewer" ? (
            // Map pages need content-container for proper sidebar margins like cfp-v1 fish-plant-viewer
            <div className="content-container">
              {children}
            </div>
          ) : location.pathname === "/" ? (
            // Home page needs full width for the background
            children
          ) : (
            // All other pages get the content container with 900px max-width
            <div className="content-container">
              {children}
            </div>
          )}
        </main>
      </div>

      {isMobile && (
        <nav className="bottom-nav" style={{ background: 'linear-gradient(to bottom right, #1f1f1f 40%, #000 100%)' }}>
          <Link to="/" className={isActiveLink('/') ? 'active' : ''} onClick={(e) => { handleRipple(e); closeSidebarOnMobile(); }}>
            <FontAwesomeIcon icon={faCampground} />
          </Link>
          <Link to="/campground-viewer" className={isActiveLink('/campground-viewer') ? 'active' : ''} onClick={(e) => { handleRipple(e); closeSidebarOnMobile(); }}>
            <FontAwesomeIcon icon={faMap} />
          </Link>
          <button className="bottom-nav-search" onClick={(e) => { handleRipple(e); handleSearchIconClick(); }}>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </button>
          <Link to="/guides" className={isActiveLink('/guides') ? 'active' : ''} onClick={(e) => { handleRipple(e); closeSidebarOnMobile(); }}>
            <FontAwesomeIcon icon={faBookOpen} />
          </Link>
          <Link to="/contact-us" className={isActiveLink('/contact-us') ? 'active' : ''} onClick={(e) => { handleRipple(e); closeSidebarOnMobile(); }}>
            <FontAwesomeIcon icon={faEnvelope} />
          </Link>
        </nav>
      )}

      <footer style={{
        padding: isMobile ? '12px 10px 68px' : '12px 10px',
        background: 'linear-gradient(to bottom right, #1f1f1f 40%, #000 100%)',  // Updated gradient
        color: '#d6e1ec',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px'
      }}>
        {/* Social Icons */}
        <div style={{ 
          display: 'flex', 
          gap: '15px',
          justifyContent: 'center',
          paddingTop: '4px'  // Small breathing room at top
        }}>
          <a href="https://www.instagram.com/sierraoutdoorsorg/" target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faInstagram} /></a>
          <a href="https://www.threads.net/@sierraoutdoorsorg" target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faThreads} /></a>
          <a href="https://www.tiktok.com/@sierraoutdoors" target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faTiktok} /></a>
          <a href="https://www.facebook.com/SierraOutdoorsOrg/" target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faFacebook} /></a>
          <a href="https://www.youtube.com/@sierraoutdoors" target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faYoutube} /></a>
          <a href="mailto:hello@sierraoutdoors.org"><FontAwesomeIcon icon={faEnvelope} /></a>
        </div>

        {/* Links */}
        <div style={{
          display: 'flex',
          gap: '15px',
          justifyContent: 'center',
          fontSize: '0.8rem'
        }}>
          <Link to="/about-us" style={{ color: '#d6e1ec', textDecoration: 'none', ":hover": { textDecoration: 'underline' } }} onClick={closeSidebarOnMobile}>About Us</Link>
          <Link to="/partner-with-us" style={{ color: '#d6e1ec', textDecoration: 'none', ":hover": { textDecoration: 'underline' } }} onClick={closeSidebarOnMobile}>Partner With Us</Link>
          <Link to="/privacy-policy" style={{ color: '#d6e1ec', textDecoration: 'none', ":hover": { textDecoration: 'underline' } }} onClick={closeSidebarOnMobile}>Privacy Policy</Link>
        </div>
        <div style={{
          display: 'flex',
          gap: '15px',
          justifyContent: 'center',
          fontSize: '0.8rem'
        }}>
          <Link to="/cookies-policy" style={{ color: '#d6e1ec', textDecoration: 'none', ":hover": { textDecoration: 'underline' } }} onClick={closeSidebarOnMobile}>Cookies Policy</Link>
          <Link to="/terms-and-conditions" style={{ color: '#d6e1ec', textDecoration: 'none', ":hover": { textDecoration: 'underline' } }} onClick={closeSidebarOnMobile}>Terms & Conditions</Link>
          <Link to="/disclaimer" style={{ color: '#d6e1ec', textDecoration: 'none', ":hover": { textDecoration: 'underline' } }} onClick={closeSidebarOnMobile}>Disclaimer</Link>
        </div>

        {/* Copyright */}
        <div style={{ 
          fontSize: '0.75rem',
          textAlign: 'center',
          paddingBottom: '4px'  // Small breathing room at bottom
        }}>
          Copyright 2025 - Sierra Outdoors
        </div>
      </footer>

      <CookieConsent />
      {!user && !loading && showLoginPopup && !isHomePage && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '100%', maxWidth: '450px', zIndex: 1000 }}>
          <div style={{ position: 'relative', width: '100%', height: '600px', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.3)', border: '1px solid white', backgroundColor: 'black' }}>
            <img
              src={bgImages[currentBgIndex]}
              alt=""
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'opacity 1s ease-in-out',
                opacity: isTransitioning ? 0 : 1,
                zIndex: 0
              }}
            />
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1 }} />
            <div style={{ position: 'relative', zIndex: 2, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '20px', boxSizing: 'border-box' }}>
              <button onClick={() => { setShowLoginPopup(false); localStorage.setItem('lastSignupPopupDismissed', Date.now().toString()); }} style={{ position: 'absolute', top: '8px', right: '16px', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
              <h3 style={{ fontSize: '2rem', fontWeight: '500', margin: 0, marginBottom: '1rem', textAlign: 'center', fontFamily: "'Geist', sans-serif", letterSpacing: '-0.07em', color: 'white' }}>
                Sign up <span style={{ textDecoration: 'underline', textDecorationColor: '#2eb488' }}>free</span> for the best of California camping:
              </h3>
              <div style={{ width: '100%', maxWidth: '400px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px', marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.9rem', margin: 0, fontFamily: "'Geist Mono', monospace", color: 'white' }}>📬 New campground idea every Saturday at 7AM</p>
                <p style={{ fontSize: '0.9rem', margin: 0, fontFamily: "'Geist Mono', monospace", color: 'white' }}>🗺️ Interactive GPS-ready maps</p>
                <p style={{ fontSize: '0.9rem', margin: 0, fontFamily: "'Geist Mono', monospace", color: 'white' }}>📍 Must-see spots and local tips</p>
                <p style={{ fontSize: '0.9rem', margin: 0, fontFamily: "'Geist Mono', monospace", color: 'white' }}>🔐 Exclusive tools and content</p>
              </div>
              <form onSubmit={handleSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ position: 'relative', width: '100%' }}>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem 1.25rem',
                      paddingRight: '130px',
                      fontSize: '1rem',
                      border: '2px solid #e0e0e0',
                      borderRadius: '9999px',
                      outline: 'none',
                      backgroundColor: 'white',
                      fontFamily: "'Geist Mono', monospace",
                      color: '#333',
                      caretColor: '#333',
                      cursor: 'text'
                    }}
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      position: 'absolute',
                      right: '4px',
                      top: '4px',
                      bottom: '4px',
                      padding: '0.5rem 1.25rem',
                      backgroundColor: '#2eb488',
                      color: 'white',
                      border: '2px solid white',
                      borderRadius: '9999px',
                      fontSize: '0.875rem',
                      cursor: isSubmitting ? 'wait' : 'pointer',
                      opacity: isSubmitting ? 0.7 : 1,
                      transition: 'opacity 0.2s, background-color 0.2s',
                      fontFamily: "'Geist Mono', monospace",
                      textTransform: 'uppercase',
                      fontWeight: '500',
                      letterSpacing: '0.025em',
                      width: '110px'
                    }}
                  >
                    {isSubmitting ? '...' : 'SIGN UP'}
                  </button>
                </div>
                <div style={{ marginTop: '12px', minHeight: '3rem', width: '100%' }}>
                  {error && <p style={{ color: '#dc3545', fontSize: '0.875rem', textAlign: 'center' }}>{error}</p>}
                  {submitSuccess && (
                    <div style={{
                      maxWidth: '400px',
                      margin: '0 auto',
                      textAlign: 'center',
                      color: 'white',
                      fontFamily: "'Geist Mono', monospace",
                      textTransform: 'uppercase'
                    }}>
                      <p style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>GOT IT!</p>
                      <p>NOW CHECK YOUR EMAIL.</p>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
